import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2, Check, Wallet, Package, Receipt, X } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import { SeccionDetalle, DatoDetalle } from './DetailDrawer';
import { EntityLink } from './EntityLink';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Comprobante } from '@/components/tienda/Comprobante';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, fecha, porcentaje } from '@/lib/formato-tienda';

/**
 * LOS PANELES DE DETALLE — venta, cliente y producto.
 *
 * Viven juntos a propósito: son los tres que se abren uno desde el otro, y
 * tenerlos en el mismo archivo hace obvio que las relaciones cierran. Una
 * venta lleva a su clienta y a sus productos; un producto lleva a las ventas
 * donde aparece; una clienta lleva a sus compras.
 *
 * Cada uno pide SUS datos. No se pasan por props desde la tabla: la fila de
 * una tabla trae lo justo para dibujarse, y el panel necesita más. Pedirlo
 * aquí evita que la tabla cargue de más para un panel que casi nunca se abre.
 */

// ═══════════════════════════════════════════════════════════════════════════

export function DetalleDeVenta({ id }: { id: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [devolviendo, setDevolviendo] = useState(false);
  const [comprobante, setComprobante] = useState(false);

  const { data: venta, isLoading } = useQuery({
    queryKey: ['venta', id],
    queryFn: () => tienda.leerVenta(id),
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['ventas'] });
    void qc.invalidateQueries({ queryKey: ['venta', id] });
    void qc.invalidateQueries({ queryKey: ['inventario'] });
    void qc.invalidateQueries({ queryKey: ['productos'] });
    void qc.invalidateQueries({ queryKey: ['tienda'] });
  };

  const confirmar = useMutation({
    mutationFn: () => tienda.confirmarVenta(id),
    onSuccess: () => {
      toast.success('Venta confirmada. La existencia ya bajó.');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const cobrar = useMutation({
    mutationFn: () => tienda.marcarCobrada(id, true),
    onSuccess: () => {
      toast.success('Marcada como cobrada.');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  if (isLoading) return <Cargando />;
  if (!venta) return <p className="text-sm text-slate-500">No se encontró la venta.</p>;

  const piezas = venta.lineas.reduce((a, l) => a + l.cantidad, 0);
  const bruto = venta.lineas.reduce((a, l) => a + l.unitPrice * l.cantidad - l.descuento, 0);

  return (
    <div>
      <SeccionDetalle
        titulo="La venta"
        accion={
          <div className="flex gap-1.5">
            {/* El comprobante solo tiene sentido con la venta confirmada: un
                borrador todavía se puede cambiar, y un papel de algo que
                cambió después es peor que no haberlo dado. */}
            {venta.estado === 'CONFIRMADA' && (
              <Button size="sm" variant="outline" onClick={() => setComprobante(true)}>
                <Receipt className="h-3.5 w-3.5" />
                Comprobante
              </Button>
            )}
            {venta.estado === 'BORRADOR' && (
              <Button size="sm" onClick={() => confirmar.mutate()} disabled={confirmar.isPending}>
                <Check className="h-3.5 w-3.5" />
                Confirmar
              </Button>
            )}
            {venta.estado === 'CONFIRMADA' && !venta.cobrado && (
              <Button size="sm" variant="outline" onClick={() => cobrar.mutate()}>
                <Wallet className="h-3.5 w-3.5" />
                Cobrada
              </Button>
            )}
          </div>
        }
      >
        <DatoDetalle etiqueta="Estado">
          <Badge
            variant={
              venta.estado === 'CONFIRMADA'
                ? 'success'
                : venta.estado === 'ANULADA'
                  ? 'danger'
                  : 'default'
            }
          >
            {venta.estado === 'CONFIRMADA'
              ? 'Confirmada'
              : venta.estado === 'ANULADA'
                ? 'Anulada'
                : 'Borrador'}
          </Badge>
        </DatoDetalle>
        {venta.estado === 'CONFIRMADA' && (
          <DatoDetalle etiqueta="Cobro">
            {venta.cobrado ? (
              <Badge variant="success">Cobrada</Badge>
            ) : (
              <Badge variant="warning">Por cobrar</Badge>
            )}
          </DatoDetalle>
        )}
        <DatoDetalle etiqueta="Fecha">{fecha(venta.createdAt)}</DatoDetalle>
        <DatoDetalle etiqueta="Forma de pago">{venta.formaDePago ?? '—'}</DatoDetalle>
        {venta.direccion && <DatoDetalle etiqueta="Entrega">{venta.direccion}</DatoDetalle>}
      </SeccionDetalle>

      {venta.cliente && (
        <SeccionDetalle titulo="Cliente">
          <EntityLink
            tipo="cliente"
            id={venta.cliente.id}
            etiqueta={venta.cliente.nombre}
            detalle={venta.cliente.telefono ?? undefined}
          />
        </SeccionDetalle>
      )}

      <SeccionDetalle titulo={`Lo que lleva (${piezas})`}>
        <div className="space-y-1.5">
          {venta.lineas.map((l) => (
            <div key={l.id} className="flex items-start justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="tabular-nums text-slate-500">{l.cantidad}×</span>{' '}
                <EntityLink
                  tipo="producto"
                  id={l.productVariant.product.id}
                  etiqueta={l.productVariant.product.name}
                  detalle={l.productVariant.product.sku ?? undefined}
                  sinIcono
                />
                <span className="text-slate-500"> · {l.productVariant.label}</span>
                {l.descuento > 0 && (
                  <span className="ml-1 text-xs text-amber-600">−{dinero(l.descuento)}</span>
                )}
              </span>
              <span className="shrink-0 tabular-nums">
                {dinero(l.unitPrice * l.cantidad - l.descuento)}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-3 border-t border-surface-border pt-2">
          <DatoDetalle etiqueta="Subtotal" numerico>
            {dinero(bruto)}
          </DatoDetalle>
          {venta.descuento > 0 && (
            <DatoDetalle etiqueta="Descuento" numerico>
              −{dinero(venta.descuento)}
            </DatoDetalle>
          )}
          {venta.envio > 0 && (
            <DatoDetalle etiqueta="Envío" numerico>
              {dinero(venta.envio)}
            </DatoDetalle>
          )}
          {venta.estado !== 'BORRADOR' && (
            <div className="mt-1 flex items-baseline justify-between border-t border-surface-border pt-2 text-sm font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{dinero(venta.total)}</span>
            </div>
          )}
        </div>
      </SeccionDetalle>

      {venta.estado === 'CONFIRMADA' && (
        <SeccionDetalle
          titulo="Devolver"
          accion={
            !devolviendo && (
              <Button size="sm" variant="outline" onClick={() => setDevolviendo(true)}>
                <Undo2 className="h-3.5 w-3.5" />
                Devolver
              </Button>
            )
          }
        >
          {devolviendo ? (
            <FormularioDeDevolucion
              saleId={id}
              onCerrar={() => setDevolviendo(false)}
              onHecho={() => {
                setDevolviendo(false);
                invalidar();
              }}
            />
          ) : (
            <p className="text-xs text-slate-500">
              La clienta compró varias y devuelve una. Para devolver todo, usa «Anular todo».
            </p>
          )}
        </SeccionDetalle>
      )}

      {venta.notas && (
        <SeccionDetalle titulo="Notas">
          <p className="text-sm text-slate-600 dark:text-slate-300">{venta.notas}</p>
        </SeccionDetalle>
      )}

      {comprobante && <Comprobante ventaId={venta.id} onCerrar={() => setComprobante(false)} />}
    </div>
  );
}

function FormularioDeDevolucion({
  saleId,
  onCerrar,
  onHecho,
}: {
  saleId: string;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const toast = useToast();
  const [cant, setCant] = useState<Record<string, number>>({});
  const [motivo, setMotivo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['devolvible', saleId],
    queryFn: () => tienda.devolvible(saleId),
  });

  const enviar = useMutation({
    mutationFn: () =>
      tienda.devolver(
        saleId,
        Object.entries(cant)
          .filter(([, c]) => c > 0)
          .map(([productVariantId, cantidad]) => ({ productVariantId, cantidad })),
        motivo.trim(),
      ),
    onSuccess: () => {
      toast.success('Devolución registrada. La mercancía volvió al inventario.');
      onHecho();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo devolver'),
  });

  if (isLoading) return <Cargando />;
  const lineas = (data ?? []).filter((d) => d.sePuedeDevolver > 0);
  if (lineas.length === 0) {
    return <p className="text-sm text-slate-500">Ya se devolvió todo lo de esta venta.</p>;
  }

  const algo = Object.values(cant).some((c) => c > 0);

  return (
    <div className="space-y-3">
      {lineas.map((d) => (
        <div key={d.productVariantId} className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm">
            {d.producto} <span className="text-slate-500">· {d.talla}</span>
            <span className="ml-1 text-xs text-slate-400">
              (llevó {d.vendido}
              {d.devuelto > 0 && `, devolvió ${d.devuelto}`})
            </span>
          </span>
          <Input
            type="number"
            className="w-20"
            value={String(cant[d.productVariantId] ?? 0)}
            onChange={(e) =>
              setCant((p) => ({
                ...p,
                [d.productVariantId]: Math.max(
                  0,
                  Math.min(d.sePuedeDevolver, Number(e.target.value) || 0),
                ),
              }))
            }
          />
        </div>
      ))}

      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-200">
          ¿Por qué la devolvió? <span className="text-red-600">*</span>
        </label>
        <Input
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="La talla le quedó chica (tallaje chino)"
        />
        <p className="mt-1 text-xs text-slate-500">
          Si una talla vuelve siempre, esto es lo que te lo va a explicar cuando toque decidir si
          la vuelves a comprar.
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button
          size="sm"
          onClick={() => enviar.mutate()}
          disabled={!algo || !motivo.trim() || enviar.isPending}
        >
          {enviar.isPending ? 'Registrando…' : 'Registrar devolución'}
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

export function DetalleDeCliente({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['cliente', id],
    queryFn: () => tienda.fichaDeCliente(id),
  });

  if (isLoading) return <Cargando />;
  if (!data) return <p className="text-sm text-slate-500">No se encontró la clienta.</p>;

  const { cliente, ventas, resumen } = data;

  return (
    <div>
      <SeccionDetalle titulo="Datos">
        <DatoDetalle etiqueta="Teléfono">{cliente.telefono ?? '—'}</DatoDetalle>
        <DatoDetalle etiqueta="Dirección">{cliente.direccion ?? '—'}</DatoDetalle>
        <DatoDetalle etiqueta="Zona">{cliente.zona ?? '—'}</DatoDetalle>
      </SeccionDetalle>

      <SeccionDetalle titulo="Resumen">
        <DatoDetalle etiqueta="Compras" numerico>
          {numero(resumen.compras)}
        </DatoDetalle>
        <DatoDetalle etiqueta="Total comprado" numerico>
          {dinero(resumen.totalComprado)}
        </DatoDetalle>
        {/* Las veces Y la plata. El conteo dice si esta clienta devuelve mucho;
            el monto dice cuánto de lo comprado se deshizo. Uno sin el otro
            deja la pregunta a medias. */}
        <DatoDetalle etiqueta="Devoluciones" numerico>
          {resumen.devoluciones === 0 ? (
            numero(0)
          ) : (
            <span className="text-amber-600 dark:text-amber-400">
              {numero(resumen.devoluciones)} · {dinero(resumen.montoDevuelto)}
            </span>
          )}
        </DatoDetalle>
        <DatoDetalle etiqueta="Te debe" numerico>
          {resumen.porCobrar > 0 ? (
            <span className="text-amber-600 dark:text-amber-400">{dinero(resumen.porCobrar)}</span>
          ) : (
            dinero(0)
          )}
        </DatoDetalle>
        <DatoDetalle etiqueta="Última compra">
          {resumen.ultimaCompra ? fecha(resumen.ultimaCompra) : 'nunca'}
        </DatoDetalle>
      </SeccionDetalle>

      <SeccionDetalle titulo={`Sus compras (${ventas.length})`}>
        {ventas.length === 0 ? (
          <p className="text-sm text-slate-400">Todavía no ha comprado.</p>
        ) : (
          <div className="space-y-1.5">
            {ventas.slice(0, 20).map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 text-sm">
                <EntityLink tipo="venta" id={v.id} etiqueta={v.numero} detalle={fecha(v.createdAt)} />
                <span className="shrink-0 tabular-nums text-slate-600 dark:text-slate-300">
                  {v.estado === 'BORRADOR' ? '—' : dinero(v.total)}
                </span>
              </div>
            ))}
          </div>
        )}
      </SeccionDetalle>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

export function DetalleDeProducto({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['productos', 'detalle', id],
    queryFn: async () => {
      const todos = await tienda.listarProductos({ limit: 200 });
      return todos.find((p) => p.id === id) ?? null;
    },
  });

  if (isLoading) return <Cargando />;
  if (!data) return <p className="text-sm text-slate-500">No se encontró el producto.</p>;

  const stock = data.variants.reduce((a, v) => a + v.stock, 0);
  const margen = data.cost != null && data.price > 0 ? (data.price - data.cost) / data.price : null;

  return (
    <div>
      <SeccionDetalle titulo="Datos">
        <DatoDetalle etiqueta="Código">
          <span className="font-mono">{data.sku ?? '—'}</span>
        </DatoDetalle>
        <DatoDetalle etiqueta="Categoría">{data.category ?? '—'}</DatoDetalle>
        <DatoDetalle etiqueta="Precio" numerico>
          {dinero(data.price)}
        </DatoDetalle>
        <DatoDetalle etiqueta="Costo" numerico>
          {data.cost == null ? (
            <span className="text-amber-600 dark:text-amber-400">sin costo cargado</span>
          ) : (
            dinero(data.cost)
          )}
        </DatoDetalle>
        <DatoDetalle etiqueta="Margen" numerico>
          {margen == null ? '—' : porcentaje(margen)}
        </DatoDetalle>
        <DatoDetalle etiqueta="Piezas" numerico>
          {numero(stock)}
        </DatoDetalle>
      </SeccionDetalle>

      <SeccionDetalle titulo="Tallas">
        <div className="flex flex-wrap gap-1.5">
          {data.variants.map((v) => (
            <span
              key={v.id}
              className={
                v.stock <= 0
                  ? 'rounded-lg border border-surface-border bg-slate-50 px-2 py-1 text-sm text-slate-400 dark:bg-slate-800'
                  : v.lowStockThreshold > 0 && v.stock <= v.lowStockThreshold
                    ? 'rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
                    : 'rounded-lg border border-surface-border px-2 py-1 text-sm'
              }
            >
              {v.label} <span className="tabular-nums">{v.stock}</span>
            </span>
          ))}
        </div>
      </SeccionDetalle>

      {data.aliasesJson?.length > 0 && (
        <SeccionDetalle titulo="También le dicen">
          <p className="text-sm text-slate-600 dark:text-slate-300">{data.aliasesJson.join(', ')}</p>
        </SeccionDetalle>
      )}

      <FotosDelProducto productoId={id} fotos={data.imagesJson ?? []} />
    </div>
  );
}

/**
 * LAS FOTOS QUE EL AGENTE LE MANDA A LA CLIENTA.
 *
 * No son decoración del panel: son las que salen por WhatsApp cuando alguien
 * pide ver un modelo. Por eso el pie lo dice — sin esa frase, se sube una foto
 * mal recortada pensando que solo la ve una.
 */
function FotosDelProducto({ productoId, fotos }: { productoId: string; fotos: string[] }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [subiendo, setSubiendo] = useState(false);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['productos'] });
  };

  const subir = async (archivos: FileList | null) => {
    if (!archivos || archivos.length === 0) return;
    setSubiendo(true);
    try {
      // Una por una y en orden: el servidor lee un archivo por petición, y en
      // paralelo dos subidas podrían leer la misma lista y pisarse.
      for (const archivo of Array.from(archivos)) {
        await tienda.subirFotoDeProducto(productoId, archivo);
      }
      toast.success(archivos.length === 1 ? 'Foto subida' : `${archivos.length} fotos subidas`);
      refrescar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo subir');
    } finally {
      setSubiendo(false);
    }
  };

  const quitar = async (key: string) => {
    try {
      await tienda.quitarFotoDeProducto(productoId, key);
      toast.success('Quitada del catálogo. El archivo no se borra.');
      refrescar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo quitar');
    }
  };

  return (
    <SeccionDetalle
      titulo={`Fotos (${fotos.length})`}
      accion={
        <label className="cursor-pointer text-xs font-medium text-brand-700 hover:underline dark:text-brand-400">
          {subiendo ? 'Subiendo…' : 'Subir fotos'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            disabled={subiendo}
            onChange={(e) => {
              void subir(e.target.files);
              // Se limpia para poder volver a elegir el MISMO archivo: sin
              // esto, subir, quitar y volver a subir no dispara el evento.
              e.target.value = '';
            }}
          />
        </label>
      }
    >
      {fotos.length === 0 ? (
        <p className="text-xs text-slate-500">
          Sin fotos. El agente no puede mandar este modelo aunque la clienta lo pida por su nombre.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {fotos.map((key) => (
              <div key={key} className="group relative">
                <img
                  src={tienda.urlDeFoto(key)}
                  alt=""
                  className="h-20 w-20 rounded-lg border border-surface-border object-cover"
                />
                <button
                  type="button"
                  onClick={() => void quitar(key)}
                  title="Quitar del catálogo"
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-900/80 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Estas son las que el agente manda por WhatsApp cuando piden ver el modelo. Van todas,
            una detrás de otra.
          </p>
        </>
      )}
    </SeccionDetalle>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

export function DetalleDePaca({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['paca', id],
    queryFn: () => tienda.leerPaca(id),
  });

  if (isLoading) return <Cargando />;
  if (!data) return <p className="text-sm text-slate-500">No se encontró la paca.</p>;

  return (
    <div>
      <SeccionDetalle titulo="La paca">
        <DatoDetalle etiqueta="Suplidor">{data.suplidor?.nombre ?? '—'}</DatoDetalle>
        <DatoDetalle etiqueta="Llegó">{fecha(data.fecha)}</DatoDetalle>
        <DatoDetalle etiqueta="Costó" numerico>
          {dinero(data.costoTotal)}
        </DatoDetalle>
        <DatoDetalle etiqueta="Piezas declaradas" numerico>
          {data.piezasDeclaradas == null ? '—' : numero(data.piezasDeclaradas)}
        </DatoDetalle>
        <DatoDetalle etiqueta="Estado">
          {data.cerradaAt ? (
            <Badge variant="success">Costo repartido</Badge>
          ) : (
            <Badge variant="warning">Sin repartir</Badge>
          )}
        </DatoDetalle>
      </SeccionDetalle>

      <SeccionDetalle titulo={`Piezas (${data.productos?.length ?? 0})`}>
        {!data.productos || data.productos.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-slate-400">
            <Package className="h-4 w-4" />
            Todavía no se le cargaron piezas.
          </p>
        ) : (
          <div className="space-y-1.5">
            {data.productos.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-sm">
                <EntityLink
                  tipo="producto"
                  id={p.id}
                  etiqueta={p.name}
                  detalle={p.sku ?? undefined}
                />
                <span className="shrink-0 tabular-nums text-slate-600 dark:text-slate-300">
                  {p.cost == null ? (
                    <span className="text-amber-600">sin costo</span>
                  ) : (
                    dinero(p.cost)
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </SeccionDetalle>
    </div>
  );
}

function Cargando() {
  return (
    <div className="flex justify-center py-10">
      <Spinner />
    </div>
  );
}
